import { useState } from 'react';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useCopyToast } from '../../hooks/useCopyToast';
import { useCreatePersonalApiToken } from '../../hooks/usePersonalApiTokens';
import type { PersonalApiTokenExpiryDays } from '../../services/api/personalApiTokens';

export interface CreateTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EXPIRY_OPTIONS: PersonalApiTokenExpiryDays[] = [30, 90, 180, 365];
const DEFAULT_EXPIRY_DAYS: PersonalApiTokenExpiryDays = 90;

type Step = 'form' | 'reveal';

/**
 * 2-step create -> reveal modal for minting a Personal API Token (PAT-01, D-09).
 * The plaintext token returned by the create mutation lives only in this
 * component's local state AND transiently in the create mutation's TanStack
 * Query state (`createToken.data.token`) — it is never persisted
 * (localStorage/sessionStorage). `resetAndClose` clears BOTH copies
 * (local state + `createToken.reset()`, review WR-05) the moment the user
 * closes/Done's the reveal step, so it cannot be re-displayed after close
 * (T-71-12 mitigation).
 */
export function CreateTokenModal({ isOpen, onClose }: CreateTokenModalProps) {
  const [step, setStep] = useState<Step>('form');
  const [name, setName] = useState('');
  const [expiryDays, setExpiryDays] = useState<PersonalApiTokenExpiryDays>(DEFAULT_EXPIRY_DAYS);
  const [plaintextToken, setPlaintextToken] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { copied, copy, reset: resetCopied } = useCopyToast();
  const createToken = useCreatePersonalApiToken();

  const resetAndClose = () => {
    setStep('form');
    setName('');
    setExpiryDays(DEFAULT_EXPIRY_DAYS);
    setPlaintextToken(null);
    setFormError(null);
    resetCopied();
    // Review WR-05: the mutation result (`createToken.data.token`) also holds
    // the plaintext, and this component stays mounted after close (rendered
    // unconditionally by PersonalApiTokensSection) — purge the mutation state
    // so the token doesn't survive in memory / React Query devtools.
    createToken.reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError('Name is required');
      return;
    }
    createToken.mutate(
      { name: trimmedName, expiryDays },
      {
        onSuccess: (result) => {
          setPlaintextToken(result.token);
          setStep('reveal');
        },
        onError: (err: unknown) => {
          setFormError(err instanceof Error ? err.message : 'Failed to create token');
        },
      }
    );
  };

  const handleCopy = () => copy(plaintextToken);

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={step === 'form' ? 'New personal API token' : 'Token created'}
      size="md"
      footer={
        step === 'form' ? (
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={resetAndClose} disabled={createToken.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} isLoading={createToken.isPending}>
              Create token
            </Button>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button onClick={resetAndClose}>Done</Button>
          </div>
        )
      }
    >
      {step === 'form' ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="pat-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              id="pat-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="e.g. Slack bot"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="pat-expiry" className="block text-sm font-medium text-gray-700 mb-1">
              Expires in
            </label>
            <select
              id="pat-expiry"
              value={expiryDays}
              onChange={(e) =>
                setExpiryDays(Number(e.target.value) as PersonalApiTokenExpiryDays)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {EXPIRY_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </select>
          </div>
          {(formError || createToken.isError) && (
            <div role="alert" className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {formError || 'Failed to create token'}
            </div>
          )}
        </form>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
            You won&apos;t be able to see this token again — copy it now.
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono text-gray-900 break-all"
              data-testid="pat-plaintext"
            >
              {plaintextToken}
            </div>
            <Button variant="secondary" onClick={handleCopy} className="flex items-center gap-2">
              {copied ? (
                <>
                  <CheckIcon className="h-4 w-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
